// Student view — dedicated "My assignments" page reached from the left sidebar.
// Grouped by assignment card (title, instructor, due date, progress). Click a
// card → detail page where items are attempted in order.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ChevronRight, ClipboardList, Loader2 } from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';

function formatDue(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const today = new Date();
  const days = Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (days < 0) return `overdue by ${Math.abs(days)}d`;
  if (days === 0) return 'due today';
  if (days === 1) return 'due tomorrow';
  return `due in ${days}d`;
}

export default function MyAssignmentsPage() {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.myAssignments();
        setAssignments(res.data.assignments || []);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Could not load assignments.');
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <AppShell><div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></AppShell>;
  if (error) return <AppShell><p className="text-sm text-rose-600">{error}</p></AppShell>;

  const open = assignments.filter((a) => !a.completed);
  const done = assignments.filter((a) => a.completed);

  return (
    <AppShell>
      <div className="border-b border-slate-200 pb-4">
        <p className="mono-stat">Assigned to you</p>
        <h1 className="mt-1 font-display text-3xl font-semibold text-slate-900">My assignments</h1>
        <p className="mt-1 text-sm text-slate-500">Practice packages your instructor prepared. Click a card to attempt the items in sequence.</p>
      </div>

      {assignments.length === 0 && (
        <div className="mt-10 text-center" data-testid="assignments-empty">
          <ClipboardList className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">Nothing assigned yet. Your instructor will drop practice here when they do.</p>
        </div>
      )}

      {open.length > 0 && (
        <section className="mt-6 space-y-3" data-testid="assignments-open">
          {open.map((a) => {
            const pct = a.total_count ? Math.round((a.completed_count / a.total_count) * 100) : 0;
            return (
              <Link
                key={a.assignment_id}
                to={`/assignments/${a.assignment_id}`}
                data-testid={`assignment-card-${a.assignment_id}`}
                className="group flex items-center gap-4 rounded border border-slate-200 bg-white p-5 transition hover:border-indigo-400 hover:shadow"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-display text-[17px] font-semibold text-slate-900">{a.title}</p>
                    {a.due_at && (
                      <span className={cn(
                        'rounded-full px-2 py-0.5 text-[11px] font-medium',
                        new Date(a.due_at) < new Date() ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-600'
                      )}>{formatDue(a.due_at)}</span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">Assigned by {a.assigned_by_name}</p>
                  {a.description && <p className="mt-2 max-w-prose text-sm italic text-slate-600">“{a.description}”</p>}
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full bg-indigo-500" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="mono-stat">{a.completed_count} / {a.total_count}</span>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 shrink-0 text-slate-300 group-hover:text-indigo-500" />
              </Link>
            );
          })}
        </section>
      )}

      {done.length > 0 && (
        <details className="mt-8" data-testid="assignments-done-toggle">
          <summary className="cursor-pointer text-xs font-medium text-slate-500">Completed ({done.length})</summary>
          <ul className="mt-2 space-y-2">
            {done.map((a) => (
              <li key={a.assignment_id} data-testid={`assignment-done-${a.assignment_id}`}>
                <Link
                  to={`/assignments/${a.assignment_id}`}
                  className="flex items-center gap-3 rounded border border-slate-100 bg-slate-50 px-4 py-2 text-sm text-slate-600 hover:border-slate-300"
                >
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
                  <span className="min-w-0 flex-1 truncate">{a.title}</span>
                  <span className="text-xs text-slate-400">{a.total_count} items</span>
                </Link>
              </li>
            ))}
          </ul>
        </details>
      )}
    </AppShell>
  );
}
